import TextButtonIcon from "@@/components/Button/TextButtonIcon";
import { CardColors } from "@@/components/Card/CardProps";
import { ProcessCard, ProcessCardProps } from "@@/components/Card/Process";
import Scroller, { ScrollerItem } from "@@/components/Scroller";
import { SideHeader } from "@@/components/SideHeader";
import Box from "@@/elements/Box";
import Container from "@@/elements/Container";
import SvgArrowRight from "@@/elements/Icons/icons/ArrowRight";
import Typography from "@@/elements/Typography";
import OptimizelyBlock, {
  OptimizelyBlockProps,
} from "@@/integrations/optimizly/Block";
import EpiContent from "@@/integrations/optimizly/Content";
import EpiProperty from "@@/integrations/optimizly/Property";
import { LinkData } from "@@/models/linkdata";
import { useEffect, useState } from "react";
import { TokenValue as bps } from "../../../design-tokens/breakpoints";
import styles from "./index.module.scss";

export type ProcessCardContainerProperties = {
  header: string;
  label?: string;
  headerVariant?: "left" | "center";
};

export type ProcessCardContainerOwnProps = {
  cards: ProcessCardProps[];
  sliderDesktop?: boolean;
  sliderMobile?: boolean;
  link?: LinkData;
  cardColor?: CardColors;
  anchorId?: string;
};

export type ProcessCardContainerProps = ProcessCardContainerOwnProps &
  ProcessCardContainerProperties;

const ProcessCardContainer = ({
  header,
  cards,
  sliderDesktop,
  sliderMobile,
  link,
  cardColor = "blue",
  label,
  headerVariant = "center",
  anchorId,
}: ProcessCardContainerProps) => {
  const [slider, setSlider] = useState<boolean>(false);
  const [shouldShowSideHeader, setShouldShowSideHeader] = useState<boolean>(false);



  useEffect(() => {
    const checkLayout = () => {
      if (
        (sliderMobile && window.innerWidth < bps.Large) ||
        (sliderDesktop && window.innerWidth >= bps.Large)
      ) {
        setSlider(true);
      } else setSlider(false);

      setShouldShowSideHeader(
        sliderDesktop && 
        window.innerWidth >= bps.Large && 
        headerVariant === "left",
      );
    };
    checkLayout();

    if (typeof window !== "undefined") {
      window.addEventListener("resize", checkLayout);

      return () => {
        window.removeEventListener("resize", checkLayout);
      };
    }
  }, [sliderDesktop, sliderMobile, headerVariant]);

  if (shouldShowSideHeader) {
    return (
      <Box id={anchorId} component="section">
        <SideHeader
          header={header}
          label={label}
          link={link}>
          <Scroller
            fullPageStretch
            className={styles.cards_wrapper}>
            {cards?.map((card, index) => (
              <ScrollerItem
                key={index}>
                <ProcessCard
                  header={card.header}
                  icon={card.icon}
                  body={card.body}
                  color={cardColor}
                />
              </ScrollerItem>
            ))}
          </Scroller>
        </SideHeader>
      </Box>
    );
  }

  return (
    <Container id={anchorId}>
      {header && headerVariant === "center" && (
        <Box
          container
          width="full"
          mb={[10, 12, 14]}
          className={styles.process_header}
        >
          <hr aria-hidden="true" className={styles.line} />
          <EpiProperty<ProcessCardContainerProperties> name="header">
            <Box
              className={styles.header}
              textAlign="center"
              mx={2}
              justifyContent="center"
            >
              <Typography
                variant="heading4"
                asElement="h2">
                {header}
              </Typography>
            </Box>
          </EpiProperty>
          <hr aria-hidden="true" className={styles.line} />
        </Box>
      )}
     {header && headerVariant === "left" && (
       <Box className={styles.process_left_header}>
         <EpiProperty<ProcessCardContainerProperties> name="label">
           <div>
            {label && (
              <>
              <Typography
               textcolor="dark"
               variant="pretitle"
               asElement="div">
               {label}
             </Typography>
             <hr className={styles.centered_line} />
             </>
            )}
             
           </div>
         </EpiProperty>
         <EpiProperty<ProcessCardContainerProperties> name="header">
           <Typography
             mb={[10, 12, 14]}
             variant="heading1"
             asElement="h2"
           >
             {header}
           </Typography>
         </EpiProperty>
       </Box>
     )}
      
      {!slider && (
        <EpiContent>
          <Box
            className={
              cards.length < 4 ? styles.process_container : styles.process_grid
            }
          >
            {cards &&
              cards.map((card, index) => (
                <Box
                  container
                  flexDirection="column"
                  alignItems="center"
                  key={index}>
                  <ProcessCard
                    header={card.header}
                    icon={card.icon}
                    body={card.body}
                    color={cardColor}
                  />
                </Box>
              ))}
          </Box>
        </EpiContent>
      )}
      {slider && (
        <EpiContent>
          <Box>
            <Scroller
              fullPageStretch
              className={styles.cards_wrapper}
              fullWidthItemsMobile={false}>
              {cards?.map((card, index) => (
                <ScrollerItem
                  key={index}>
                  <ProcessCard
                    header={card.header}
                    icon={card.icon}
                    body={card.body}
                    color={cardColor}
                  />
                </ScrollerItem>
              ))}
            </Scroller>
          </Box>
        </EpiContent>
      )}
      {link && link.text && (
        <Box
          container
          justifyContent={slider ? "center" : "flex-end"}
          mt={8}>
          <TextButtonIcon
            direction="right"
            link={link}
            label={link.text}>
            <SvgArrowRight />
          </TextButtonIcon>
        </Box>
      )}
    </Container>
  );
};

export type ProvidedProcessCardContainerProps =
  OptimizelyBlockProps<ProcessCardContainerProperties> &
    ProcessCardContainerProps;

const ProvidedProcessCardContainer = ({
  blockId,
  inEditMode,
  properties,
  anchorId,
  ...rest
}: ProvidedProcessCardContainerProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
    anchorId={anchorId}
  >
    {(optimizlyProperties) => (
      <ProcessCardContainer
        anchorId={anchorId}
        {...rest}
        {...optimizlyProperties} />
    )}
  </OptimizelyBlock>
);

export default ProvidedProcessCardContainer;
